import React, { useState, useEffect } from 'react';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import {
  faUserPlus, faEdit, faEnvelope, faLock, faUser, faPhone, faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ActionOtpModal from './shared/ActionOtpModal';
import { UiModal, UiButton, UiInput, UiSelect } from './ui';
import { NEW_EMPLOYEE_DEFAULTS, PERMISSION_FALLBACKS, PERMISSION_META, PermissionKey } from '../config/permissions';

interface Props {
  employee?: any | null;
  onClose: () => void;
  onSuccess: () => void;
}

const validateFields = (data: { email: string; password: string; name: string; phone: string }, isEdit: boolean, includeRequired = false) => {
  const errors: Record<string, string> = {};
  if (includeRequired) {
    if (!isEdit && !data.email) errors.email = 'กรุณากรอก Email';
    if (!isEdit && !data.password) errors.password = 'กรุณากรอกรหัสผ่าน';
    if (!data.name) errors.name = 'กรุณากรอกชื่อ';
  }
  if (!isEdit && data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'รูปแบบ Email ไม่ถูกต้อง';
  }
  if (data.password) {
    const pw = data.password;
    if (pw.length < 10) {
      errors.password = 'รหัสผ่านต้องมีอย่างน้อย 10 ตัวอักษร';
    } else if (!/[0-9]/.test(pw)) {
      errors.password = 'รหัสผ่านต้องมีตัวเลขอย่างน้อย 1 ตัว';
    } else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw)) {
      errors.password = 'รหัสผ่านต้องมีอักขระพิเศษอย่างน้อย 1 ตัว (!@#$%^&*...)';
    }
  }
  if (data.phone && !/^[0-9]{9,10}$/.test(data.phone)) {
    errors.phone = 'เบอร์โทรต้องเป็นตัวเลข 9-10 หลัก';
  }
  return errors;
};

const EmployeeFormModal: React.FC<Props> = ({ employee, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const isEdit = !!employee;

  const [formData, setFormData] = useState({
    email: isEdit ? (employee.email ?? '') : '',
    password: '',
    name: isEdit ? (employee.full_name ?? '') : '',
    phone: isEdit ? (employee.phone ?? '') : '',
    role: isEdit ? (employee.role ?? 'employee') : 'employee',
    // edit: เติม key ใหม่ที่ไม่มีในข้อมูลเก่าด้วยค่า fallback (ไม่ใช่ค่า default ของพนักงานใหม่)
    permissions: isEdit
      ? { ...PERMISSION_FALLBACKS, ...(employee.permissions ?? {}) }
      : { ...NEW_EMPLOYEE_DEFAULTS }
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showOtp, setShowOtp] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);

  const { email, password, phone, name } = formData;
  useEffect(() => {
    const timer = setTimeout(() => {
      setFieldErrors(validateFields({ email, password, phone, name }, isEdit));
    }, 500);
    return () => clearTimeout(timer);
  }, [email, password, phone, name, isEdit]);

  const setPermission = (key: PermissionKey, value: boolean) => {
    setFormData((prev) => ({ ...prev, permissions: { ...prev.permissions, [key]: value } }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateFields(formData, isEdit, true);
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }

    if (isEdit) {
      const payload: any = {
        name: formData.name,
        phone: formData.phone,
        role: formData.role,
        permissions: formData.permissions
      };
      if (formData.password) payload.password = formData.password;
      setPendingPayload(payload);
      setShowOtp(true);
    } else {
      submitCreate();
    }
  };

  const submitCreate = async () => {
    try {
      await merchantApi.post('/merchants/employees', { ...formData, email: formData.email.toLowerCase() });
      toast.success(t('employee.createSuccess'));
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.error'));
    }
  };

  const handleEditVerified = async (actionToken: string) => {
    try {
      await merchantApi.put(`/merchants/employees/${employee!.id}`, { ...pendingPayload, action_token: actionToken });
      toast.success(t('employee.editSuccess'));
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.error'));
    }
  };

  return (
    <>
      <UiModal
        open
        onClose={onClose}
        title={isEdit ? t('employee.editEmployee') : t('employee.addEmployee')}
        icon={isEdit ? faEdit : faUserPlus}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
        scrollable
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <UiInput
            label={`Email${isEdit ? '' : ' *'}`}
            icon={faEnvelope}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required={!isEdit}
            disabled={isEdit}
            placeholder="email@example.com"
            error={fieldErrors.email}
            className={isEdit ? 'bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-slate-400 cursor-not-allowed' : ''}
          />

          {/* Password */}
          <UiInput
            label={`Password${isEdit ? '' : ' *'}`}
            icon={faLock}
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required={!isEdit}
            placeholder="••••••••"
            error={fieldErrors.password}
            hint={isEdit ? t('auth.passwordLeaveBlank') : undefined}
          />

          {/* Name */}
          <UiInput
            label={t('employee.nameLabel')}
            icon={faUser}
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder={t('employee.namePlaceholder')}
            error={fieldErrors.name}
          />

          {/* Phone */}
          <UiInput
            label={t('employee.phoneLabel')}
            icon={faPhone}
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="0812345678"
            error={fieldErrors.phone}
          />

          {/* Role */}
          <UiSelect
            label="Role"
            icon={faBriefcase}
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          >
            <option value="employee">Employee</option>
            <option value="manager">Manager</option>
          </UiSelect>

          {/* Permissions */}
          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-3">Permissions:</p>
            <div className="space-y-3">
              {PERMISSION_META.map(({ key, labelKey }) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.permissions[key]}
                    onChange={(e) => setPermission(key, e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-500 dark:bg-slate-700 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700 dark:text-slate-200">{t(labelKey)}</span>
                </label>
              ))}
            </div>
          </div>

          <UiButton
            type="submit"
            icon={isEdit ? faEdit : faUserPlus}
            disabled={
              Object.keys(fieldErrors).length > 0 ||
              !formData.name ||
              (!isEdit && (!formData.email || !formData.password))
            }
            className="w-full justify-center"
          >
            {isEdit ? t('employee.saveChanges') : t('employee.createEmployee')}
          </UiButton>
        </form>
      </UiModal>

      {isEdit && (
        <ActionOtpModal
          isOpen={showOtp}
          onClose={() => setShowOtp(false)}
          onVerified={handleEditVerified}
          action="employee_update"
          title={t('employee.editTitle')}
          description={t('employee.editConfirm')}
        />
      )}
    </>
  );
};

export default EmployeeFormModal;
